// Terrain levels -> render height (terrain-levels design D5).
//
// The sim publishes a 0..=12 `level` per tile when the `terrain.levels`
// module is on. HexGrid hands the land tiles' coord->level map to
// setTileLevels(); elevation.ts' tileSurfaceY then reads levelSurfaceOffset()
// so every consumer (tiles, outlines, scatter, units, rivers, waterfalls...)
// re-heights through that single function. Module dark (no levels, or every
// level 0) => nothing is published and the legacy biome heights are used,
// byte-identical to before.

/** World units per terrain level. 12 levels span 1.2, matching the old massif ceiling. */
export const LEVEL_STEP = 0.1;
/** Highest sim terrain level (CTerrainLevel is u8 0..=12). */
export const MAX_LEVEL = 12;
/** Neighbour differences up to this many levels are eased into ramps; bigger ones stay cliffs. */
export const RAMP_MAX_DROP = 2;
/** How far (0..1) a tile is pulled toward its ramp-neighbours' mean. */
export const RAMP_BLEND = 0.5;

const SIDES: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/** World height of a (possibly fractional) level above level 0. Monotonic, bounded to [0, 12 * STEP]. */
export function levelHeight(level: number): number {
  const l = Number.isFinite(level) ? Math.min(MAX_LEVEL, Math.max(0, level)) : 0;
  return l * LEVEL_STEP;
}

/**
 * Ramp smoothing: pull `own` toward the mean of neighbours that differ by at
 * most RAMP_MAX_DROP levels (gentle steps become slopes); larger differences
 * contribute nothing so cliffs stay sharp. Pure; result stays within the
 * range of the inputs.
 */
export function smoothedLevel(own: number, neighbours: ReadonlyArray<number>): number {
  let sum = 0;
  for (const n of neighbours) {
    const d = n - own;
    if (Math.abs(d) <= RAMP_MAX_DROP) sum += d;
  }
  return own + (RAMP_BLEND * sum) / 6;
}

/** Per-board cache: coord key -> smoothed level height. `null` = module dark. */
let heights: Map<string, number> | null = null;

/**
 * Publish the land coord->level map (or null to clear). A missing or
 * all-zero map means the module is dark and heights stay on the biome path.
 */
export function setTileLevels(
  map: Map<string, number> | null,
  extra?: ReadonlyMap<string, number>,
): void {
  let nonTrivial = false;
  if (map) {
    for (const l of map.values()) {
      if (l > 0) {
        nonTrivial = true;
        break;
      }
    }
  }
  if (!map || !nonTrivial) {
    heights = null;
    return;
  }
  const out = new Map<string, number>();
  const nb: number[] = [];
  for (const [key, level] of map) {
    const [q, r] = key.split(',').map(Number) as [number, number];
    nb.length = 0;
    for (const [dq, dr] of SIDES) {
      const n = map.get(`${q + dq},${r + dr}`);
      if (n !== undefined) nb.push(n);
    }
    // `extra`: landlocked-wall boost (sea/wallStyle), added after smoothing.
    out.set(key, levelHeight(smoothedLevel(level, nb)) + (extra?.get(key) ?? 0));
  }
  heights = out;
}

/** True when the terrain.levels module is active for the current board. */
export function terrainLevelsActive(): boolean {
  return heights !== null;
}

/** Smoothed level height of a land tile, or undefined (module dark / tile unlevelled). */
export function levelSurfaceOffset(q: number, r: number): number | undefined {
  return heights?.get(`${q},${r}`);
}
